import { useState } from 'react';

import { useCapabilityCheck } from '@hcs/authz';
import {
  Button,
  Dropdown,
  DropdownOptionType,
  Flex,
  FlexScrollCard,
  FlexScrollCardProps,
  LinkAnchor,
  SearchInput,
  Table,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
} from '@hcs/design-system';
import { CouponRedemptionType } from '@hcs/types';
import { formatDateTime, formatMoney, formatNumber } from '@hcs/utils';

import { CouponStatus } from '../../components/CouponStatus/CouponStatus';
import { useCouponsInternal } from '../../hooks';
import { formatRedemptionType } from '../../utils';
import { CouponFormLauncher } from '../CouponForm';
import { CouponPlans } from '../CouponPlans/CouponPlans';

import styles from './CouponsTableInternal.module.css';

const dataHcName = 'coupons-table-internal';

const getRedemptionTypeOption = (redemptionType: CouponRedemptionType) => {
  return {
    label: formatRedemptionType(redemptionType),
    value: redemptionType,
  };
};

const REDEMPTION_TYPE_OPTIONS: DropdownOptionType<CouponRedemptionType | null>[] =
  [
    {
      label: 'All',
      value: null,
    },
    getRedemptionTypeOption('churn-prevention'),
    getRedemptionTypeOption('client-credit'),
    getRedemptionTypeOption('loyalty'),
    getRedemptionTypeOption('resubscribe'),
    getRedemptionTypeOption('signup'),
  ];

export const CouponsTableInternal = ({
  redemptionType: redemptionTypeProp,
  makeCouponLink,
  hideFilters,
  hideCreateCoupon,
  fitViewHeight = true,
}: {
  hideFilters?: boolean;
  hideCreateCoupon?: boolean;
  makeCouponLink?: (couponCode: string) => string;
  redemptionType?: CouponRedemptionType;
  fitViewHeight?: FlexScrollCardProps['fitViewHeight'];
}) => {
  const [search, setSearch] = useState('');
  const [redemptionType, setRedemptionType] =
    useState<CouponRedemptionType | null>(redemptionTypeProp || null);
  const { isLoading, data: coupons } = useCouponsInternal({
    search,
    redemption_type: redemptionType ?? undefined,
  });
  const { data: canCreateCoupons } = useCapabilityCheck('coupon-codes-create');
  return (
    <>
      {!hideFilters && (
        <Flex alignItems="flex-end" className={styles.Filters}>
          <SearchInput
            dataHcName="coupons-search"
            placeholder="Search by code or name"
            value={search}
            className={styles.SearchInput}
            onChange={setSearch}
          />
          <Flex direction="column" gap="5px" className={styles.RedemptionType}>
            <label className={styles.Label}>Redemption Type</label>
            <Dropdown<CouponRedemptionType | null>
              dataHcName={`${dataHcName}-redemption-type-filter`}
              options={REDEMPTION_TYPE_OPTIONS}
              value={redemptionType}
              onSelect={(redemptionType) => {
                setRedemptionType(redemptionType);
              }}
            />
          </Flex>
        </Flex>
      )}
      <FlexScrollCard
        header={
          !hideCreateCoupon && canCreateCoupons
            ? {
                height: 40,
                content: (
                  <Flex
                    fullWidth
                    justifyContent="flex-end"
                    alignItems="flex-end"
                  >
                    <CouponFormLauncher
                      trigger={
                        <Button dataHcName="create-coupon-launcher">
                          Create Coupon
                        </Button>
                      }
                    />
                  </Flex>
                ),
              }
            : undefined
        }
        fitViewHeight={fitViewHeight}
      >
        <Table
          skeletonConfig={{ rows: 3, colCount: 11, isLoading }}
          dataHcName="coupons-table"
        >
          <TableHeader>
            <TableHeaderCell sticky stickyOffset={0}>
              Code
            </TableHeaderCell>
            <TableHeaderCell align="right">Credit</TableHeaderCell>
            <TableHeaderCell>Name</TableHeaderCell>
            <TableHeaderCell>Plans</TableHeaderCell>
            <TableHeaderCell>Redemption Type</TableHeaderCell>
            <TableHeaderCell align="right">Redemptions</TableHeaderCell>
            <TableHeaderCell>Status</TableHeaderCell>
            <TableHeaderCell>Created</TableHeaderCell>
          </TableHeader>
          {coupons?.map((c) => {
            return (
              <TableRow key={c.code}>
                <TableCell sticky stickyOffset={0}>
                  {makeCouponLink ? (
                    <LinkAnchor
                      dataHcName="coupon-link"
                      to={makeCouponLink(c.code)}
                    >
                      {c.code}
                    </LinkAnchor>
                  ) : (
                    <b>{c.code}</b>
                  )}
                </TableCell>
                <TableCell align="right">
                  {formatMoney(c.amount_off_dollars)}
                </TableCell>
                <TableCell>{c.name}</TableCell>
                <TableCell>
                  <CouponPlans couponCode={c.code} />
                </TableCell>
                <TableCell>{formatRedemptionType(c.redemption_type)}</TableCell>
                <TableCell align="right">
                  {`${formatNumber(c.redemption_count)} of ${`${c.redemption_cap === 0 ? 'Unlimited' : formatNumber(c.redemption_cap)}`}`}
                </TableCell>
                <TableCell>
                  <CouponStatus coupon={c} />
                </TableCell>
                <TableCell>{formatDateTime(c.created)}</TableCell>
              </TableRow>
            );
          })}
        </Table>
      </FlexScrollCard>
    </>
  );
};
